import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { ChatMessage } from '../../lib/supabase';
import { logger } from '../../utils/logger';

// A tabela usa sender/read_at; os componentes mantêm o formato ChatMessage.
interface LiveMessageRow {
  id: string;
  conversation_id: string;
  sender: 'visitor' | 'admin' | 'bot';
  message: string;
  read_at: string | null;
  created_at: string | null;
}

const toChatMessage = (row: LiveMessageRow, visitorName = 'Visitante'): ChatMessage => ({
  id: row.id,
  conversation_id: row.conversation_id,
  sender_type: row.sender,
  sender_name: row.sender === 'admin' ? 'Atendente IX Brasil'
    : row.sender === 'visitor' ? visitorName
    : /^(✅ Um atendente|🔒 Atendimento encerrado)/.test(row.message) ? 'Sistema' : 'Assistente IX Brasil',
  message: row.message,
  is_read: row.read_at !== null,
  created_at: row.created_at || '',
});


interface QuickSuggestion {
  id: string;
  text: string;
  icon: string;
}

// Fluxo de qualificação por tipo de seguro
interface QualificationStep {
  question: string;
  field: string;
  options?: string[];
  inputType?: 'text' | 'options';
}

interface QualificationFlow {
  type: 'auto' | 'vida' | 'residencial' | 'empresarial';
  label: string;
  steps: QualificationStep[];
}

const qualificationFlows: QualificationFlow[] = [
  {
    type: 'auto',
    label: 'Seguro Auto',
    steps: [
      {
        question: '🚗 Ótimo! Vou te ajudar a encontrar o melhor seguro auto.\n\n**Qual o modelo e ano do seu veículo?**\n_(Ex: Honda Civic 2022, Fiat Strada 2021)_',
        field: 'veiculo',
        inputType: 'text',
      },
      {
        question: '📍 Entendido! Agora me diga: **em qual cidade/estado o veículo fica guardado?**',
        field: 'cidade',
        inputType: 'text',
      },
      {
        question: '🏠 O veículo fica guardado em garagem?',
        field: 'garagem',
        options: ['Sim, garagem coberta', 'Sim, garagem descoberta', 'Não, fica na rua'],
        inputType: 'options',
      },
      {
        question: '🎯 Qual tipo de cobertura você prefere?',
        field: 'cobertura',
        options: ['Cobertura completa (compreensivo)', 'Terceiros + Roubo/Furto', 'Somente terceiros'],
        inputType: 'options',
      },
    ],
  },
  {
    type: 'vida',
    label: 'Seguro de Vida',
    steps: [
      {
        question: '❤️ Excelente escolha! Proteger sua família é fundamental.\n\n**Qual a sua faixa etária?**',
        field: 'idade',
        options: ['Até 30 anos', '31 a 45 anos', '46 a 60 anos', 'Acima de 60 anos'],
        inputType: 'options',
      },
      {
        question: '👨‍👩‍👧 Você possui dependentes (cônjuge, filhos)?',
        field: 'dependentes',
        options: ['Sim, tenho dependentes', 'Não tenho dependentes'],
        inputType: 'options',
      },
      {
        question: '💼 Qual é a sua situação profissional?',
        field: 'profissao',
        options: ['CLT / Empregado', 'Autônomo / Freelancer', 'Empresário', 'Aposentado'],
        inputType: 'options',
      },
      {
        question: '💰 Qual capital segurado você tem em mente?\n_(Valor que sua família receberia em caso de sinistro)_',
        field: 'capital',
        options: ['Até R$ 100 mil', 'R$ 100 mil a R$ 300 mil', 'R$ 300 mil a R$ 500 mil', 'Acima de R$ 500 mil'],
        inputType: 'options',
      },
    ],
  },
  {
    type: 'residencial',
    label: 'Seguro Residencial',
    steps: [
      {
        question: '🏠 Perfeito! Vamos encontrar a melhor proteção para o seu lar.\n\n**O imóvel é próprio ou alugado?**',
        field: 'posse',
        options: ['Próprio', 'Alugado', 'Financiado'],
        inputType: 'options',
      },
      {
        question: '🏗️ Qual o tipo do imóvel?',
        field: 'tipo_imovel',
        options: ['Casa', 'Apartamento', 'Sítio / Chácara', 'Outro'],
        inputType: 'options',
      },
      {
        question: '📐 Qual a metragem aproximada do imóvel?',
        field: 'metragem',
        options: ['Até 60 m²', '61 a 100 m²', '101 a 200 m²', 'Acima de 200 m²'],
        inputType: 'options',
      },
      {
        question: '🛡️ Quais coberturas são mais importantes para você?',
        field: 'coberturas',
        options: ['Incêndio e explosão', 'Roubo e furto', 'Danos elétricos', 'Todas as anteriores'],
        inputType: 'options',
      },
    ],
  },
  {
    type: 'empresarial',
    label: 'Seguro Empresarial',
    steps: [
      {
        question: '🏢 Ótimo! Vamos proteger o seu negócio da forma certa.\n\n**Qual o porte da sua empresa?**',
        field: 'porte',
        options: ['MEI / Autônomo', 'Pequena empresa', 'Média empresa', 'Grande empresa'],
        inputType: 'options',
      },
      {
        question: '🏪 Qual o segmento de atuação da empresa?',
        field: 'segmento',
        options: ['Comércio / Varejo', 'Serviços', 'Indústria', 'Tecnologia / TI', 'Outro'],
        inputType: 'options',
      },
      {
        question: '📦 O que você precisa proteger principalmente?',
        field: 'protecao',
        options: ['Estoque e mercadorias', 'Equipamentos e maquinário', 'Imóvel / Sede', 'Tudo acima'],
        inputType: 'options',
      },
      {
        question: '💼 Sua empresa possui funcionários?',
        field: 'funcionarios',
        options: ['Não, sou só eu', '1 a 5 funcionários', '6 a 20 funcionários', 'Mais de 20 funcionários'],
        inputType: 'options',
      },
    ],
  },
];

const quickSuggestions: QuickSuggestion[] = [
  { id: '0', text: 'Fazer cotação grátis', icon: 'ri-rocket-line' },
  { id: '1', text: 'Quero fazer uma cotação', icon: 'ri-file-list-3-line' },
  { id: '2', text: 'Qual a taxa de corretagem?', icon: 'ri-percent-line' },
  { id: '3', text: 'Tipos de seguros disponíveis', icon: 'ri-shield-check-line' },
  { id: '4', text: 'Como funciona o processo?', icon: 'ri-question-answer-line' },
  { id: '5', text: 'Falar com atendente', icon: 'ri-customer-service-2-line' },
  { id: '6', text: 'Horário de atendimento', icon: 'ri-time-line' },
];

// Estado do fluxo de qualificação ativo
interface ActiveQualification {
  flow: QualificationFlow;
  currentStep: number;
  answers: Record<string, string>;
}

// =====================================================
// SISTEMA DE FALLBACK INTELIGENTE - NÍVEL 1
// =====================================================
// Detecta intenção parcial da mensagem do usuário
// e retorna uma keyword de fallback para buscar resposta contextual
const detectPartialIntent = (message: string): string | null => {
  const lower = message.toLowerCase().trim();
  
  // Remover acentos para melhor matching
  const normalize = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const normalized = normalize(lower);
  
  // Categoria 1: COTAÇÃO/PREÇO/VALOR
  const cotacaoKeywords = ['cotacao', 'cotar', 'orcamento', 'preco', 'valor', 'quanto custa', 'quanto sai', 'quanto fica', 'quanto e', 'simulacao', 'simular'];
  if (cotacaoKeywords.some(k => normalized.includes(k))) {
    return 'fallback_cotacao';
  }
  
  // Categoria 2: SEGURO GENÉRICO (sem especificar tipo)
  const seguroKeywords = ['seguro', 'seguros', 'protecao', 'cobertura', 'apolice'];
  const hasSeguroWord = seguroKeywords.some(k => normalized.includes(k));
  const hasTypeWord = normalized.includes('auto') || normalized.includes('vida') || normalized.includes('residencial') || normalized.includes('empresarial') || normalized.includes('carro') || normalized.includes('casa');
  if (hasSeguroWord && !hasTypeWord) {
    return 'fallback_seguro';
  }
  
  // Categoria 3: AJUDA/DÚVIDA GENÉRICA
  const ajudaKeywords = ['ajuda', 'ajudar', 'duvida', 'informacao', 'saber', 'entender', 'explicar', 'como funciona', 'o que e', 'me fala', 'gostaria de saber'];
  if (ajudaKeywords.some(k => normalized.includes(k))) {
    return 'fallback_ajuda';
  }
  
  // Categoria 4: ECONOMIA/REDUZIR/BARATO
  const economiaKeywords = ['economizar', 'economia', 'barato', 'mais barato', 'reduzir', 'diminuir', 'pagar menos', 'desconto', 'promocao', 'oferta'];
  if (economiaKeywords.some(k => normalized.includes(k))) {
    return 'fallback_economia';
  }
  
  // Categoria 5: PROBLEMA/RECLAMAÇÃO
  const problemaKeywords = ['problema', 'reclamacao', 'insatisfeito', 'nao funciona', 'ruim', 'pessimo', 'horrivel', 'reclamar'];
  if (problemaKeywords.some(k => normalized.includes(k))) {
    return 'fallback_problema';
  }
  
  // Categoria 6: CONTATO/TELEFONE/EMAIL
  const contatoKeywords = ['contato', 'telefone', 'email', 'falar', 'ligar', 'whatsapp', 'horario', 'atendimento'];
  if (contatoKeywords.some(k => normalized.includes(k))) {
    return 'fallback_contato';
  }
  
  // Categoria 7: DOCUMENTOS/PROCESSO
  const documentosKeywords = ['documento', 'documentacao', 'processo', 'contratar', 'contratacao', 'como fazer', 'passo a passo', 'preciso de'];
  if (documentosKeywords.some(k => normalized.includes(k))) {
    return 'fallback_documentos';
  }
  
  // Categoria 8: SINISTRO/ACIONAR
  const sinistroKeywords = ['sinistro', 'acionar', 'acidente', 'batida', 'roubo', 'furto', 'incendio', 'dano', 'prejuizo'];
  if (sinistroKeywords.some(k => normalized.includes(k))) {
    return 'fallback_sinistro';
  }
  
  // Categoria 9: DIFERENCIAL/COMPARAÇÃO
  const diferencialKeywords = ['diferencial', 'porque escolher', 'por que escolher', 'vantagem', 'beneficio', 'melhor', 'comparar', 'diferenca'];
  if (diferencialKeywords.some(k => normalized.includes(k))) {
    return 'fallback_diferencial';
  }
  
  // Categoria 10: RENOVAÇÃO/REVISAR
  const renovacaoKeywords = ['renovacao', 'renovar', 'revisar', 'revisao', 'ja tenho', 'tenho seguro', 'meu seguro'];
  if (renovacaoKeywords.some(k => normalized.includes(k))) {
    return 'fallback_renovacao';
  }
  
  return null;
};

export default function ChatAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatViewport, setChatViewport] = useState<{ top: number; height: number } | null>(null);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isAdminTyping, setIsAdminTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorId, setVisitorId] = useState<string>('');
  const [conversationType, setConversationType] = useState<'bot' | 'human'>('bot');
  const [conversationStatus, setConversationStatus] = useState<'active' | 'waiting' | 'closed'>('active');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showWhatsAppFallback, setShowWhatsAppFallback] = useState(false);
  const [waitingSeconds, setWaitingSeconds] = useState(0);
  const [visibleWaitingSeconds, setVisibleWaitingSeconds] = useState(0);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [activeQualification, setActiveQualification] = useState<ActiveQualification | null>(null);
  // Opções de resposta rápida do bot atual
  const [currentQuickReplies, setCurrentQuickReplies] = useState<string[] | null>(null);
  
  // =====================================================
  // SISTEMA DE FALLBACK INTELIGENTE - NÍVEL 2 e 3
  // =====================================================
  // Contador de tentativas sem match (ref para evitar closure stale)
  const noMatchCountRef = useRef(0);

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitingCounterRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Timer do admin typing como ref para não causar re-subscribe no realtime
  const adminTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const realtimeChannelRef = useRef<any>(null);
  const typingChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingReadyRef = useRef(false);
  const typingLastSentRef = useRef(0);
  const typingIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const conversationTypeRef = useRef<'bot' | 'human'>('bot');
  const conversationStatusRef = useRef<'active' | 'waiting' | 'closed'>('active');
  const navigate = useNavigate();

  useEffect(() => { conversationTypeRef.current = conversationType; }, [conversationType]);
  useEffect(() => { conversationStatusRef.current = conversationStatus; }, [conversationStatus]);


  // Mantém o chat independente do layout do site. Em telas pequenas,
  // bloqueia a rolagem da página enquanto a conversa ocupa a tela toda.
  // Também sinaliza a visibilidade para os componentes de notificações do site.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const mobileQuery = window.matchMedia('(max-width: 767px)');

    const updatePageScroll = () => {
      html.style.overflow = isOpen && mobileQuery.matches ? 'hidden' : previousHtmlOverflow;
      body.style.overflow = isOpen && mobileQuery.matches ? 'hidden' : previousBodyOverflow;
    };

    if (isOpen) {
      html.dataset.ixbrasilChatOpen = 'true';
      updatePageScroll();
      mobileQuery.addEventListener('change', updatePageScroll);
      window.dispatchEvent(new CustomEvent('ixbrasil:chat-visibility', { detail: { open: true } }));
    } else {
      delete html.dataset.ixbrasilChatOpen;
      window.dispatchEvent(new CustomEvent('ixbrasil:chat-visibility', { detail: { open: false } }));
    }

    return () => {
      mobileQuery.removeEventListener('change', updatePageScroll);
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      delete html.dataset.ixbrasilChatOpen;
      if (isOpen) window.dispatchEvent(new CustomEvent('ixbrasil:chat-visibility', { detail: { open: false } }));
    };
  }, [isOpen]);

  // A altura da área visível diminui quando o teclado do celular aparece.
  // No iOS, visualViewport é mais confiável do que apenas 100dvh.
  useEffect(() => {
    if (!isOpen) return;
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const viewport = window.visualViewport;

    const updateViewport = () => {
      if (!mobileQuery.matches || !viewport) {
        setChatViewport(null);
        return;
      }
      const next = {
        top: Math.max(0, Math.round(viewport.offsetTop)),
        height: Math.max(1, Math.round(viewport.height)),
      };
      setChatViewport((current) =>
        current?.top === next.top && current.height === next.height ? current : next
      );
    };

    updateViewport();
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    mobileQuery.addEventListener('change', updateViewport);
    return () => {
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
      mobileQuery.removeEventListener('change', updateViewport);
    };
  }, [isOpen]);

  useEffect(() => {
    let vid = localStorage.getItem('ibx_visitor_id');
    if (!vid) {
      vid = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('ibx_visitor_id', vid);
    }
    setVisitorId(vid);
  }, []);

  useEffect(() => {
  if (!isOpen) return;

  const container = messagesContainerRef.current;
  if (!container) return;

  const scrollToBottom = () => {
    container.scrollTo({
      top: container.scrollHeight,
      behavior: showWhatsAppFallback ? 'auto' : 'smooth'
    });
  };

  const frame = requestAnimationFrame(scrollToBottom);

  return () => cancelAnimationFrame(frame);
}, [messages, currentQuickReplies, isAdminTyping, showWhatsAppFallback, isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        const welcomeMessage: ChatMessage = {
          id: `welcome_${Date.now()}`,
          conversation_id: '',
          sender_type: 'bot',
          sender_name: 'Assistente IX Brasil',
          message: 'Olá! 👋 Bem-vindo à IX Brasil Seguros. Como posso ajudá-lo hoje?',
          is_read: true,
          created_at: new Date().toISOString(),
        };
        setMessages([welcomeMessage]);
      }, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem('ibx_welcome_shown');
    if (alreadyShown) return;
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowWelcomeBubble(true);
        sessionStorage.setItem('ibx_welcome_shown', 'true');
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen) setShowWelcomeBubble(false);
  }, [isOpen]);

  // No smartphone, o convite é breve. No computador, o convite original continua.
  useEffect(() => {
    if (!showWelcomeBubble || !window.matchMedia('(max-width: 767px)').matches) return;
    const timer = window.setTimeout(() => {
      setShowWelcomeBubble(false);
      setWelcomeDismissed(true);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [showWelcomeBubble]);

  const handleOpenFromBubble = () => {
    setShowWelcomeBubble(false);
    setWelcomeDismissed(true);
    setIsOpen(true);
  };

  const handleDismissBubble = () => {
    setShowWelcomeBubble(false);
    setWelcomeDismissed(true);
  };

  useEffect(() => {
    if (conversationType === 'human' && conversationStatus === 'waiting') {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (waitingCounterRef.current) clearInterval(waitingCounterRef.current);
      setWaitingSeconds(0);
      waitingCounterRef.current = setInterval(() => {
        setWaitingSeconds((prev) => prev + 1);
      }, 1000);
      inactivityTimerRef.current = setTimeout(() => {
        if (conversationTypeRef.current === 'human' && conversationStatusRef.current === 'waiting') {
          setShowWhatsAppFallback(true);
          if (waitingCounterRef.current) clearInterval(waitingCounterRef.current);
        }
      }, 60000);
      return () => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (waitingCounterRef.current) clearInterval(waitingCounterRef.current);
      };
    }
  }, [conversationType, conversationStatus]);

  useEffect(() => {
    if (conversationStatus === 'active') {
      if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
      if (waitingCounterRef.current) { clearInterval(waitingCounterRef.current); waitingCounterRef.current = null; }
      setShowWhatsAppFallback(false);
      setWaitingSeconds(0);
    }
  }, [conversationStatus]);

  // Contagem VISUAL independente: os primeiros cinco segundos continuam
  // reservados à sequência das mensagens de transferência.
  // Só inicia em 0s quando o aviso amarelo aparece.
  const showWaitingIndicator = conversationType === 'human'
    && conversationStatus === 'waiting'
    && !showWhatsAppFallback
    && waitingSeconds > 5;

  useEffect(() => {
    setVisibleWaitingSeconds(0);
    if (!showWaitingIndicator) return;
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setVisibleWaitingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [showWaitingIndicator]);

  const markConversationClosed = () => {
    // O ref bloqueia envios imediatamente, antes da próxima renderização.
    conversationStatusRef.current = 'closed';
    setConversationStatus('closed');
    setIsAdminTyping(false);
    setIsTyping(false);
    setShowWhatsAppFallback(false);
    setCurrentQuickReplies(null);
    setSendError(null);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (waitingCounterRef.current) {
      clearInterval(waitingCounterRef.current);
      waitingCounterRef.current = null;
    }
  };

  useEffect(() => {
    if (!conversationId || !supabase) return;
    if (realtimeChannelRef.current) supabase.removeChannel(realtimeChannelRef.current);
    let cancelled = false;
    let statusRevision = 0;

    const channel = supabase
      .channel(`chat_${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ibx_chat_live_messages', filter: `conversation_id=eq.${conversationId}` }, (payload) => {
        const newMessage = toChatMessage(payload.new as LiveMessageRow);
        const isClosureNotice = newMessage.sender_type === 'bot'
          && newMessage.message.startsWith('🔒 Atendimento encerrado');
        if (isClosureNotice) {
          statusRevision += 1;
          markConversationClosed();
        }
        if (newMessage.sender_type === 'admin' || isClosureNotice) {
          setMessages((prev) => {
            if (prev.some(msg => msg.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          });
          setShowWhatsAppFallback(false);
          setIsAdminTyping(false);
          lastActivityRef.current = new Date();
          playNotificationSound();
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'ibx_chat_conversations', filter: `id=eq.${conversationId}` }, (payload) => {
        const updatedConversation = payload.new as any;
        if (updatedConversation.status) {
          statusRevision += 1;
          if (updatedConversation.status === 'closed') {
            markConversationClosed();
            return;
          }
          const previousStatus = conversationStatusRef.current;
          conversationStatusRef.current = updatedConversation.status;
          setConversationStatus(updatedConversation.status);
          if (updatedConversation.status === 'active' && previousStatus === 'waiting') {
            const systemMessage: ChatMessage = {
              id: `system_${conversationId}_active`,
              conversation_id: conversationId,
              sender_type: 'bot',
              sender_name: 'Sistema',
              message: '✅ Um atendente entrou na conversa. Você já pode conversar!',
              is_read: true,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => {
         if (prev.some((msg) => msg.id === systemMessage.id)) return prev;
        return [...prev, systemMessage];
        });
          }
        }
      })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        // Recuperar um encerramento ocorrido antes da conexão ou durante uma queda.
        const revision = statusRevision;
        void (async () => {
          try {
            const { data, error } = await supabase
              .from('ibx_chat_conversations')
              .select('status')
              .eq('id', conversationId)
              .maybeSingle();
            if (cancelled || error || revision !== statusRevision) return;
            if (data?.status === 'closed') markConversationClosed();
          } catch (error) {
            logger.error('Erro ao conferir encerramento da conversa:', error);
          }
        })();
      });

    realtimeChannelRef.current = channel;
    return () => {
      cancelled = true;
      if (realtimeChannelRef.current && supabase) supabase.removeChannel(realtimeChannelRef.current);
      if (adminTypingTimerRef.current) clearTimeout(adminTypingTimerRef.current);
    };
  }, [conversationId]);

  // Sinais temporários de digitação; não alteram as mensagens nem o banco.
  useEffect(() => {
    setIsAdminTyping(false);
    if (!conversationId || conversationType !== 'human' || conversationStatus !== 'active' || !supabase) return;
    let remoteTimer: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase.channel(`chat_typing_${conversationId}`, {
      config: { broadcast: { self: false } },
    });
    typingChannelRef.current = channel;
    typingReadyRef.current = false;
    typingLastSentRef.current = 0;
    channel.on('broadcast', { event: 'typing' }, (event) => {
      if (event.payload?.sender !== 'admin') return;
      if (remoteTimer) clearTimeout(remoteTimer);
      const active = event.payload.isTyping === true;
      setIsAdminTyping(active);
      if (active) remoteTimer = setTimeout(() => setIsAdminTyping(false), 3500);
    }).subscribe((status) => {
      if (typingChannelRef.current === channel) {
        typingReadyRef.current = status === 'SUBSCRIBED';
      }
    });
    return () => {
      if (remoteTimer) clearTimeout(remoteTimer);
      if (typingIdleTimerRef.current) clearTimeout(typingIdleTimerRef.current);
      if (typingChannelRef.current === channel) {
        typingChannelRef.current = null;
        typingReadyRef.current = false;
      }
      void supabase.removeChannel(channel);
    };
  }, [conversationId, conversationType, conversationStatus]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => { /* bloqueado pelo navegador */ });
    } catch (_e) { /* som bloqueado pelo navegador */ }
  };

  const detectHumanIntent = (message: string): boolean => {
    const keywords = ['atendente','atendimento','pessoa','humano','falar com alguém','falar com alguem','preciso de ajuda','ajuda','suporte','representante','operador','consultor','vendedor','corretor'];
    const lower = message.toLowerCase();
    return keywords.some((k) => lower.includes(k));
  };

  // Detectar qual tipo de seguro o visitante quer
  const detectInsuranceType = (message: string): QualificationFlow | null => {
    const lower = message.toLowerCase();
    if (lower.includes('seguro auto') || lower.includes('seguro carro') || lower.includes('seguro veículo') || lower.includes('automóvel') || lower.includes('carro')) {
      return qualificationFlows.find(f => f.type === 'auto') || null;
    }
    if (lower.includes('seguro de vida') || lower.includes('seguro vida') || lower.includes('vida')) {
      return qualificationFlows.find(f => f.type === 'vida') || null;
    }
    if (lower.includes('seguro residencial') || lower.includes('seguro casa') || lower.includes('seguro imóvel') || lower.includes('seguro apartamento') || lower.includes('residencial') || lower.includes('casa') || lower.includes('apartamento')) {
      return qualificationFlows.find(f => f.type === 'residencial') || null;
    }
    if (lower.includes('seguro empresarial') || lower.includes('seguro empresa') || lower.includes('seguro negócio') || lower.includes('empresarial') || lower.includes('empresa') || lower.includes('negócio') || lower.includes('mei')) {
      return qualificationFlows.find(f => f.type === 'empresarial') || null;
    }
    return null;
  };

  const getAutomaticResponse = async (message: string): Promise<{ text: string; quickReplies?: string[] } | null> => {
    if (!supabase) return null;
    try {
      // Primeiro: tentar match exato com keywords
      const { data, error } = await supabase.rpc('get_chat_response', { user_message: message.toLowerCase() });
      if (error) throw error;
      
      // Se encontrou resposta, resetar contador de no-match
      if (data) {
        noMatchCountRef.current = 0;
        return { text: data };
      }
      
      // =====================================================
      // NÍVEL 1: Fallback por intenção detectada
      // =====================================================
      // Se não encontrou match exato, tentar detectar intenção parcial
      const partialIntent = detectPartialIntent(message);
      if (partialIntent) {
        const { data: fallbackData, error: fallbackError } = await supabase.rpc('get_chat_response', { user_message: partialIntent });
        if (!fallbackError && fallbackData) {
          noMatchCountRef.current = 0;
          return { text: fallbackData };
        }
      }
      
      // =====================================================
      // NÍVEL 2: Fallback com sugestões (primeira tentativa sem match)
      // =====================================================
      const currentCount = noMatchCountRef.current;
      noMatchCountRef.current = currentCount + 1;
      
      if (currentCount === 0) {
        // Primeira tentativa sem match: oferecer sugestões
        return {
          text: 'Desculpe, não entendi exatamente sua pergunta. 😊\n\n**Posso te ajudar com:**',
          quickReplies: ['Fazer cotação grátis', 'Tipos de seguros', 'Como economizar', 'Falar com consultor']
        };
      }
      
      // =====================================================
      // NÍVEL 3: Fallback final (segunda tentativa sem match)
      // =====================================================
      if (currentCount >= 1) {
        // Segunda tentativa sem match: oferecer WhatsApp ou atendente
        return {
          text: 'Percebi que está com dificuldade para encontrar o que precisa. 🤔\n\n**Vamos facilitar:**\n\nPosso te conectar com um **consultor especializado** que vai te atender pessoalmente e esclarecer todas as suas dúvidas!\n\nOu se preferir, continue pelo **WhatsApp** para atendimento mais rápido.',
          quickReplies: ['Falar com consultor', 'Continuar no WhatsApp', 'Fazer cotação grátis']
        };
      }
      
      return null;
    } catch (error) {
      logger.error('Erro ao buscar resposta automática:', error);
      return null;
    }
  };

  const createConversation = async (type: 'bot' | 'human') => {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from('ibx_chat_conversations')
        .insert({ visitor_id: visitorId, visitor_name: leadData.name || null, visitor_email: leadData.email || null, visitor_phone: leadData.phone || null, status: type === 'human' ? 'waiting' : 'active', type })
        .select()
        .single();
      if (error) throw error;
      setConversationId(data.id);
      setConversationType(type);
      setConversationStatus(data.status);
      return data.id;
    } catch (error) {
      logger.error('Erro ao criar conversa:', error);
      return null;
    }
  };

  const upgradeToHumanConversation = async () => {
    if (!conversationId || !supabase) return null;
    try {
      const { data, error } = await supabase
        .from('ibx_chat_conversations')
        .update({ type: 'human', status: 'waiting', visitor_name: leadData.name || null, visitor_email: leadData.email || null, visitor_phone: leadData.phone || null })
        .eq('id', conversationId)
        .select()
        .single();
      if (error) throw error;
      setConversationType('human');
      setConversationStatus('waiting');
      return data.id;
    } catch (error) {
      logger.error('Erro ao atualizar conversa:', error);
      return null;
    }
  };

  const saveMessage = async (convId: string, senderType: 'visitor' | 'admin' | 'bot', message: string, senderName: string | null = null, optimisticId?: string) => {
    if (senderType === 'visitor' && conversationStatusRef.current === 'closed') return null;
    if (!supabase) return null;
    try {
      const { data, error } = await supabase.from('ibx_chat_live_messages').insert({ conversation_id: convId, sender: senderType, message, read_at: senderType === 'visitor' ? null : new Date().toISOString() }).select().single();
      if (error) throw error;
      await supabase.from('ibx_chat_conversations').update({ last_message: message, last_message_at: new Date().toISOString() }).eq('id', convId);
      return toChatMessage(data as LiveMessageRow, senderName || 'Visitante');
    } catch (error) {
      setSendError('Não foi possível enviar a mensagem. Tente novamente.');
      setIsTyping(false);
      if (senderType === 'visitor') {
        setInputMessage((current) => current || message);
        setMessages((prev) => {
          const failedIndex = prev.findIndex((msg) => msg.id === optimisticId);
          return prev.filter((_, index) => index !== failedIndex);
        });
      }
      logger.error('Erro ao salvar mensagem:', error);
      return null;
    }
  };

  const sendHumanAttendanceAlert = async (convId: string) => {
    if (!supabase) return;
    try {
      await supabase.functions.invoke('send-chat-alert', { body: { conversationId: convId, visitorData: { name: leadData.name || null, email: leadData.email || null, phone: leadData.phone || null } } });
    } catch (error) {
      logger.error('Erro ao enviar alerta:', error);
    }
  };

  const notifyVisitorTyping = async (isTyping = true) => {
    if (typingIdleTimerRef.current) clearTimeout(typingIdleTimerRef.current);
    const channel = typingChannelRef.current;
    if (!channel || !typingReadyRef.current) return;
    const sendSignal = async (active: boolean) => {
      try {
        const result = await channel.send({
          type: 'broadcast', event: 'typing',
          payload: { sender: 'visitor', isTyping: active },
        });
        if (result !== 'ok') logger.error('Falha no sinal de digitação:', result);
      } catch (error) {
        logger.error('Erro no sinal de digitação:', error);
      }
    };
    if (!isTyping) {
      typingLastSentRef.current = 0;
      await sendSignal(false);
      return;
    }
    const now = Date.now();
    if (!typingLastSentRef.current || now - typingLastSentRef.current >= 1000) {
      typingLastSentRef.current = now;
      void sendSignal(true);
    }
    typingIdleTimerRef.current = setTimeout(() => {
      typingLastSentRef.current = 0;
      void sendSignal(false);
    }, 3000);
  };

  // Adicionar mensagem do bot ao chat
  const addBotMessage = (text: string, convId: string, quickReplies?: string[]) => {
    const botMsg: ChatMessage = {
      id: `bot_${Date.now()}`,
      conversation_id: convId,
      sender_type: 'bot',
      sender_name: 'Assistente IX Brasil',
      message: text,
      is_read: true,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, botMsg]);
    setCurrentQuickReplies(quickReplies || null);
    if (convId) saveMessage(convId, 'bot', text, 'Assistente IX Brasil');
  };

  // Avançar no fluxo de qualificação
  const advanceQualification = async (qualification: ActiveQualification, answer: string, convId: string) => {
    const updatedAnswers = { ...qualification.answers, [qualification.flow.steps[qualification.currentStep].field]: answer };
    const nextStep = qualification.currentStep + 1;

    if (nextStep < qualification.flow.steps.length) {
      // Próxima pergunta
      const nextStepData = qualification.flow.steps[nextStep];
      setActiveQualification({ ...qualification, currentStep: nextStep, answers: updatedAnswers });
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addBotMessage(nextStepData.question, convId, nextStepData.options);
      }, 1000);
    } else {
      // Fluxo concluído — resumo + CTA para formulário
      setActiveQualification(null);
      setIsTyping(true);

      const summaryLines = Object.entries(updatedAnswers).map(([key, val]) => {
        const labels: Record<string, string> = {
          veiculo: '🚗 Veículo', cidade: '📍 Cidade', garagem: '🏠 Garagem', cobertura: '🎯 Cobertura',
          idade: '🎂 Faixa etária', dependentes: '👨‍👩‍👧 Dependentes', profissao: '💼 Profissão', capital: '💰 Capital segurado',
          posse: '🏠 Imóvel', tipo_imovel: '🏗️ Tipo', metragem: '📐 Metragem', coberturas: '🛡️ Coberturas',
          porte: '🏢 Porte', segmento: '🏪 Segmento', protecao: '📦 Proteção', funcionarios: '👥 Funcionários',
        };
        return `${labels[key] || key}: ${val}`;
      }).join('\n');

      const finalMsg = `✅ Perfeito! Coletei todas as informações necessárias para o seu **${qualification.flow.label}**:\n\n${summaryLines}\n\n🎯 Agora é só preencher o formulário de cotação com esses dados e nossa equipe retorna com as melhores opções do mercado para você!\n\n👉 [Acessar formulário de cotação](/cotacao)`;

      setTimeout(() => {
        setIsTyping(false);
        addBotMessage(finalMsg, convId, ['Acessar formulário agora', 'Falar com consultor']);
      }, 1200);
    }
  };

  const handleSendMessage = async (overrideMessage?: string) => {
    if (conversationStatusRef.current === 'closed') return;
    const userMessage = (overrideMessage || inputMessage).trim();
    if (!userMessage) return;
    setSendError(null);
    void notifyVisitorTyping(false);
    setInputMessage('');
    setCurrentQuickReplies(null);
    lastActivityRef.current = new Date();

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      conversation_id: conversationId || '',
      sender_type: 'visitor',
      sender_name: leadData.name || 'Visitante',
      message: userMessage,
      is_read: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Ação especial: acessar formulário
    if (userMessage === 'Acessar formulário agora') {
      navigate('/cotacao');
      setIsOpen(false);
      return;
    }
    
    // Ação especial: continuar no WhatsApp
    if (userMessage === 'Continuar no WhatsApp') {
      handleWhatsAppRedirect();
      return;
    }

    // Ação especial: falar com consultor
    if (userMessage === 'Falar com consultor') {
      setIsTyping(true);
      let convId = conversationId;
      if (convId) { convId = await upgradeToHumanConversation(); }
      else { convId = await createConversation('human'); }
      if (convId) {
        if (!await saveMessage(convId, 'visitor', userMessage, leadData.name || null, userMsg.id)) return;
        await sendHumanAttendanceAlert(convId);
        setTimeout(() => {
          addBotMessage('✅ Entendido! Estou transferindo você para um consultor especializado. Por favor, aguarde um momento. ⏳', convId || '');
          setIsTyping(false);
          setTimeout(() => {
            addBotMessage('⏱️ Tempo estimado de espera: até 5 minutos.\n\nNossos atendentes estão disponíveis de segunda a sexta, das 8h às 18h, e aos sábados das 8h às 12h.\n\nCaso prefira, você também pode nos contatar diretamente pelo WhatsApp. 😊', convId || '');
          }, 2000);
        }, 1000);
      }
      return;
    }

    // Se há um fluxo de qualificação ativo, processar resposta
    if (activeQualification && conversationType === 'bot') {
      let convId = conversationId;
      if (!convId) convId = await createConversation('bot');
      if (convId) { if (!await saveMessage(convId, 'visitor', userMessage, leadData.name || null, userMsg.id)) return; }
      await advanceQualification(activeQualification, userMessage, convId || '');
      return;
    }

    // Detectar intenção de atendimento humano
    if (detectHumanIntent(userMessage) && conversationType === 'bot') {
      setIsTyping(true);
      let convId = conversationId;
      if (convId) { convId = await upgradeToHumanConversation(); }
      else { convId = await createConversation('human'); }
      if (convId) {
        if (!await saveMessage(convId, 'visitor', userMessage, leadData.name || null, userMsg.id)) return;
        await sendHumanAttendanceAlert(convId);
        setTimeout(() => {
          addBotMessage('✅ Entendi que você deseja falar com um atendente. Estou transferindo você para nossa equipe. Por favor, aguarde um momento enquanto conectamos você com um especialista. ⏳', convId || '');
          setIsTyping(false);
          setTimeout(() => {
            addBotMessage('⏱️ Tempo estimado de espera: até 5 minutos.\n\nNossos atendentes estão disponíveis de segunda a sexta, das 8h às 18h, e aos sábados das 8h às 12h.\n\nCaso prefira, você também pode nos contatar diretamente pelo WhatsApp. 😊', convId || '');
          }, 2500);
        }, 1000);
      }
      return;
    }

    // Modo bot
    if (conversationType === 'bot') {
      setIsTyping(true);
      let convId = conversationId;
      if (!convId) convId = await createConversation('bot');
      if (convId) { if (!await saveMessage(convId, 'visitor', userMessage, leadData.name || null, userMsg.id)) return; }

      // Verificar se é um tipo de seguro para iniciar qualificação
      const insuranceFlow = detectInsuranceType(userMessage);
      if (insuranceFlow) {
        const firstStep = insuranceFlow.steps[0];
        const newQualification: ActiveQualification = { flow: insuranceFlow, currentStep: 0, answers: {} };
        setActiveQualification(newQualification);
        noMatchCountRef.current = 0; // Resetar contador ao iniciar qualificação
        setTimeout(() => {
          setIsTyping(false);
          addBotMessage(firstStep.question, convId || '', firstStep.options);
        }, 1000);
        return;
      }

      const response = await getAutomaticResponse(userMessage);
      setTimeout(() => {
        setIsTyping(false);
        if (response) {
          addBotMessage(response.text, convId || '', response.quickReplies);
        } else {
          // Fallback final caso tudo falhe
          addBotMessage('Desculpe, estou com dificuldade para processar sua mensagem. Por favor, tente reformular ou fale com um de nossos atendentes. 😊', convId || '', ['Falar com consultor', 'Fazer cotação grátis']);
        }
      }, 1500);
    } else {
      if (conversationId) {
        if (!await saveMessage(conversationId, 'visitor', userMessage, leadData.name || null, userMsg.id)) return;
      }
    }
  };

  const handleQuickSuggestion = (text: string) => {
    if (text === 'Fazer cotação grátis') {
      navigate('/cotacao');
      setIsOpen(false);
      return;
    }
    
    // Resetar contador ao clicar em sugestão
    noMatchCountRef.current = 0;
    handleSendMessage(text);
  };

  // Clique em quick reply do bot (durante qualificação ou resposta)
  const handleQuickReply = (reply: string) => {
    if (reply === 'Acessar formulário agora') {
      navigate('/cotacao');
      setIsOpen(false);
      return;
    }
    
    // Resetar contador ao clicar em quick reply
    noMatchCountRef.current = 0;
    handleSendMessage(reply);
  };

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadData.name || !leadData.email || !leadData.phone) return;
    if (conversationId && supabase) {
      try {
        await supabase.from('ibx_chat_conversations').update({ visitor_name: leadData.name, visitor_email: leadData.email, visitor_phone: leadData.phone }).eq('id', conversationId);
      } catch (error) {
        logger.error('Erro ao atualizar dados do lead:', error);
      }
    }
    setShowLeadForm(false);
    const leadMessage: ChatMessage = {
      id: `lead_${Date.now()}`,
      conversation_id: conversationId || '',
      sender_type: 'bot',
      sender_name: 'Assistente IX Brasil',
      message: `Obrigado, ${leadData.name}! Seus dados foram registrados com sucesso. Como posso ajudá-lo? 😊`,
      is_read: true,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, leadMessage]);
  };

  const renderMessageWithLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const boldRegex = /\*\*([^*]+)\*\*/g;

    // Processar bold e links juntos
    const processText = (raw: string): (string | JSX.Element)[] => {
      const parts: (string | JSX.Element)[] = [];
      let lastIndex = 0;

      // Combinar regex de links e bold
      const combinedRegex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
      let match;

      while ((match = combinedRegex.exec(raw)) !== null) {
        if (match.index > lastIndex) parts.push(raw.slice(lastIndex, match.index));

        if (match[1] && match[2]) {
          // Link
          const [, label, href] = match;
          const isInternal = href.startsWith('/');
          parts.push(
            <button
              key={`link_${match.index}`}
              onClick={() => { if (isInternal) { navigate(href); setIsOpen(false); } else { window.open(href, '_blank'); } }}
              className="inline-flex items-center gap-1 bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap my-1"
            >
              <i className="ri-external-link-line text-sm"></i>
              {label}
            </button>
          );
        } else if (match[3]) {
          // Bold
          parts.push(<strong key={`bold_${match.index}`} className="font-semibold">{match[3]}</strong>);
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < raw.length) parts.push(raw.slice(lastIndex));
      return parts;
    };

    return processText(text);
  };

  // Inicia um atendimento independente depois que a conversa anterior foi encerrada.
  // O histórico anterior permanece no Supabase; apenas o estado local do visitante é reiniciado.
  const handleNewConversation = () => {
    if (conversationStatusRef.current !== 'closed') return;
    conversationStatusRef.current = 'active';
    conversationTypeRef.current = 'bot';
    setConversationId(null);
    setConversationType('bot');
    setConversationStatus('active');
    setShowWhatsAppFallback(false);
    setWaitingSeconds(0);
    setIsAdminTyping(false);
    setIsTyping(false);
    setShowLeadForm(false);
    setCurrentQuickReplies(null);
    setActiveQualification(null);
    setInputMessage('');
    setSendError(null);
    noMatchCountRef.current = 0;
    setMessages([{
      id: `welcome_${Date.now()}`,
      conversation_id: '',
      sender_type: 'bot',
      sender_name: 'Assistente IX Brasil',
      message: 'Olá! 👋 Bem-vindo à IX Brasil Seguros. Como posso ajudá-lo hoje?',
      is_read: true,
      created_at: new Date().toISOString(),
    }]);
  };

  const handleWhatsAppRedirect = () => {
    const phone = '5577988080505';
    const message = encodeURIComponent(`Olá! Vim do chat do site da IX Brasil Seguros e gostaria de continuar o atendimento. ${leadData.name ? `Meu nome é ${leadData.name}.` : ''}`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    if (conversationId && supabase) {
      supabase.from('ibx_chat_conversations').update({ status: 'closed', closed_reason: 'redirected_to_whatsapp' }).eq('id', conversationId);
    }
  };

  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (waitingCounterRef.current) clearInterval(waitingCounterRef.current);
      if (adminTypingTimerRef.current) clearTimeout(adminTypingTimerRef.current);
      if (realtimeChannelRef.current && supabase) supabase.removeChannel(realtimeChannelRef.current);
    };
  }, []);

  // Indicador de progresso da qualificação
  const renderQualificationProgress = () => {
    if (!activeQualification) return null;
    const { flow, currentStep } = activeQualification;
    const total = flow.steps.length;
    const progress = Math.round((currentStep / total) * 100);
    return (
      <div className="px-4 py-2 bg-teal-50 border-b border-teal-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-teal-700 font-medium">
            <i className="ri-shield-check-line mr-1"></i>
            Qualificando: {flow.label}
          </span>
          <span className="text-xs text-teal-600">{currentStep}/{total} perguntas</span>
        </div>
        <div className="w-full bg-teal-100 rounded-full h-1.5">
          <div
            className="bg-teal-500 h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    );
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 md:bottom-6 md:right-6">
        {showWelcomeBubble && (
          <>
          {/* Convite compacto exclusivo de smartphone, próximo ao botão do chat. */}
          <div className="absolute bottom-1 right-[calc(100%+0.75rem)] w-52 max-w-[calc(100vw-7rem)] rounded-xl border border-gray-100 bg-white p-3 shadow-xl animate-fadeIn md:hidden">
            <div className="absolute -right-1.5 bottom-5 h-3 w-3 rotate-45 border-r border-t border-gray-100 bg-white" aria-hidden="true" />
            <div className="relative flex items-start gap-1">
              <button type="button" onClick={handleOpenFromBubble} className="min-w-0 flex-1 text-left" aria-label="Conversar com a IX Brasil">
                <span className="block text-xs font-semibold leading-snug text-gray-800">Precisa de ajuda com seu seguro?</span>
                <span className="mt-1 block text-xs leading-snug text-teal-700">Fale com a IX Brasil.</span>
              </button>
              <button type="button" onClick={handleDismissBubble} className="flex h-6 w-6 flex-none items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Fechar convite do chat">
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          {/* A apresentação original continua somente no computador. */}
          <div className="relative hidden max-w-[280px] rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl animate-fadeIn md:block">
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 rotate-45"></div>
            <button onClick={handleDismissBubble} className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer" aria-label="Fechar">
              <i className="ri-close-line text-sm"></i>
            </button>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-line text-white text-base"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Assistente IX Brasil</p>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  <span className="text-xs text-green-600">Online agora</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              🎯 Faça uma <strong>cotação grátis em 2 minutos</strong> e descubra quanto você pode economizar no seu seguro agora mesmo!
            </p>
            <button onClick={handleOpenFromBubble} className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm font-medium py-2 px-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap">
              🚀 Cotar grátis agora →
            </button>
          </div>
          </>
        )}
        <button onClick={() => setIsOpen(true)} className="w-14 h-14 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-full shadow-2xl hover:shadow-teal-500/50 hover:scale-110 transition-all duration-300 flex items-center justify-center group" aria-label="Abrir chat">
          <i className="ri-message-3-line text-2xl group-hover:scale-110 transition-transform"></i>
          {!welcomeDismissed && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>}
        </button>
      </div>
    );
  }

  // Portal: não herda o posicionamento nem o limite de largura dos elementos do site.
  // No celular, ocupa toda a viewport; no computador, mantém a janela flutuante.
  return createPortal(
    <div
      role="dialog"
      aria-label="Chat da IX Brasil Seguros"
      style={chatViewport ? { top: chatViewport.top, height: chatViewport.height, bottom: 'auto' } : undefined}
      className="fixed inset-0 z-[2147483000] flex h-screen h-[100dvh] w-full min-w-0 flex-col overflow-hidden overscroll-contain bg-white shadow-2xl md:inset-auto md:bottom-6 md:right-6 md:h-[min(620px,calc(100dvh-3rem))] md:w-[min(28rem,calc(100vw-3rem))] md:rounded-2xl"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 flex items-center justify-between flex-shrink-0" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className={`text-xl ${conversationType === 'human' ? 'ri-customer-service-2-line' : 'ri-robot-line'}`}></i>
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm">IX Brasil Seguros</h3>
            <div className="flex items-center gap-2">
              <p className="text-xs text-teal-100">
                {conversationStatus === 'closed' ? 'Atendimento encerrado' : conversationType === 'human'
                  ? conversationStatus === 'waiting' ? 'Aguardando atendente...' : 'Atendimento Humano'
                  : activeQualification ? `Cotando: ${activeQualification.flow.label}` : 'Assistente Virtual'}
              </p>
              {conversationType === 'human' && conversationStatus === 'active' && (
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              )}
            </div>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" aria-label="Fechar chat">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      {/* Barra de progresso da qualificação */}
      {renderQualificationProgress()}

      {/* Messages */}
      <div
      ref={messagesContainerRef}
      className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-3 md:p-4 space-y-4 bg-gray-50">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender_type === 'visitor' ? 'justify-end' : 'justify-start'}`}>
            <div className={`min-w-0 max-w-[88%] md:max-w-[82%] rounded-2xl px-4 py-2.5 ${msg.sender_type === 'visitor' ? 'bg-teal-500 text-white rounded-br-none' : 'bg-white text-gray-800 shadow-sm rounded-bl-none'}`} style={{ overflowWrap: 'anywhere' }}>
              {msg.sender_type !== 'visitor' && (
                <p className="text-xs font-medium text-teal-600 mb-1">{msg.sender_name}</p>
              )}
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {msg.sender_type !== 'visitor' ? renderMessageWithLinks(msg.message) : msg.message}
              </p>
              <p className={`text-xs mt-1 ${msg.sender_type === 'visitor' ? 'text-teal-100' : 'text-gray-400'}`}>
                {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {sendError && (
          <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{sendError}</p>
        )}

        {/* Indicador de digitação do bot */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
              </div>
            </div>
          </div>
        )}

        {/* Indicador de digitação do admin */}
        {isAdminTyping && conversationType === 'human' && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
              <p className="text-xs text-teal-600 font-medium mb-1">Atendente está digitando...</p>
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
              </div>
            </div>
          </div>
        )}

        {/* Quick replies do bot (opções de resposta rápida durante qualificação) */}
        {conversationStatus !== 'closed' && currentQuickReplies && !isTyping && conversationType === 'bot' && (
          <div className="flex flex-wrap gap-2 justify-start pl-1">
            {currentQuickReplies.map((reply) => (
              <button
                key={reply}
                onClick={() => handleQuickReply(reply)}
                className="bg-white hover:bg-teal-50 border border-teal-200 hover:border-teal-400 text-teal-700 text-xs font-medium px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md"
              >
                {reply}
              </button>
            ))}
          </div>
        )}

        {/* Sugestões rápidas iniciais */}
        {messages.length === 1 && !isTyping && conversationType === 'bot' && !activeQualification && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 text-center">Sugestões rápidas:</p>
            <button
              onClick={() => handleQuickSuggestion('Fazer cotação grátis')}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl p-3 flex items-center gap-3 transition-all shadow-md hover:shadow-teal-400/40 group cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                <i className="ri-rocket-line text-xl text-white"></i>
              </div>
              <div className="flex-1 text-left">
                <span className="text-sm font-semibold block">🎯 Fazer cotação grátis</span>
                <span className="text-xs text-teal-100">Em 2 minutos, sem compromisso</span>
              </div>
              <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">GRÁTIS</span>
            </button>

            {/* Tipos de seguro para qualificação direta */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Seguro Auto', icon: 'ri-car-line', msg: 'seguro auto' },
                { label: 'Seguro de Vida', icon: 'ri-heart-pulse-line', msg: 'seguro de vida' },
                { label: 'Seguro Residencial', icon: 'ri-home-4-line', msg: 'seguro residencial' },
                { label: 'Seguro Empresarial', icon: 'ri-building-2-line', msg: 'seguro empresarial' },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleQuickSuggestion(item.msg)}
                  className="bg-white hover:bg-teal-50 border border-gray-200 hover:border-teal-300 rounded-xl p-3 text-left transition-all group cursor-pointer"
                >
                  <i className={`${item.icon} text-teal-500 text-lg mb-1 block`}></i>
                  <span className="text-xs text-gray-700 group-hover:text-teal-700 font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {quickSuggestions.slice(2).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleQuickSuggestion(suggestion.text)}
                  className="bg-white hover:bg-teal-50 border border-gray-200 hover:border-teal-300 rounded-xl p-3 text-left transition-all group cursor-pointer"
                >
                  <i className={`${suggestion.icon} text-teal-500 text-lg mb-1 block`}></i>
                  <span className="text-xs text-gray-700 group-hover:text-teal-700">{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fallback WhatsApp */}
        {showWhatsAppFallback && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center animate-fadeIn">
            <i className="ri-whatsapp-line text-3xl text-green-600 mb-2"></i>
            <p className="text-sm text-gray-700 mb-3">Parece que nossos atendentes estão ocupados no momento. Que tal continuar pelo WhatsApp para um atendimento mais rápido?</p>
            <button onClick={handleWhatsAppRedirect} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2 cursor-pointer">
              <i className="ri-whatsapp-line text-lg"></i>
              Continuar no WhatsApp
            </button>
          </div>
        )}

        {/* Formulário de Lead */}
        {showLeadForm && (
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-200 animate-fadeIn">
            <h4 className="font-semibold text-gray-800 mb-3 text-sm">Para melhor atendê-lo, preencha seus dados:</h4>
            <form onSubmit={handleLeadFormSubmit} className="space-y-3">
              <input type="text" placeholder="Seu nome completo" value={leadData.name} onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" required />
              <input type="email" placeholder="Seu e-mail" value={leadData.email} onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" required />
              <input type="tel" placeholder="Seu telefone (com DDD)" value={leadData.phone} onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" required />
              <button type="submit" className="w-full bg-teal-500 hover:bg-teal-600 text-white py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer">Enviar Dados</button>
            </form>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Contador fixo: não fica escondido pela rolagem das mensagens. */}
      {showWaitingIndicator && (
        <div className="flex flex-shrink-0 justify-center border-t border-gray-100 bg-gray-50 px-3 py-2">
          <span className="inline-flex max-w-full items-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-700">
            <i className="ri-time-line mr-1" aria-hidden="true"></i>
            Aguardando atendente... {visibleWaitingSeconds}s
          </span>
        </div>
      )}

      {/* Opções após o encerramento: fixas acima da área de digitação. */}
      {conversationStatus === 'closed' && (
        <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-100 bg-white px-3 py-3">
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Este atendimento foi encerrado. Você pode iniciar uma nova conversa ou continuar pelo WhatsApp.
          </p>
          <button type="button" onClick={handleNewConversation}
            className="w-full rounded-lg bg-teal-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-teal-700">
            Iniciar nova conversa
          </button>
          <button type="button" onClick={handleWhatsAppRedirect}
            className="w-full rounded-lg bg-green-500 px-3 py-2.5 text-sm font-semibold text-white hover:bg-green-600">
            Falar no WhatsApp
          </button>
        </div>
      )}

      {/* Input */}
      <div className="p-3 md:p-4 bg-white border-t border-gray-200 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}>
        {activeQualification && (
          <p className="text-xs text-teal-600 mb-2 flex items-center gap-1">
            <i className="ri-information-line"></i>
            Selecione uma opção acima ou digite sua resposta
          </p>
        )}
        <div className="flex gap-2">
          {conversationStatus !== 'closed' && !showLeadForm && messages.length > 1 && !leadData.name && (
            <button onClick={() => setShowLeadForm(true)} className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-teal-500 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer" title="Preencher dados de contato" aria-label="Preencher dados">
              <i className="ri-user-add-line text-xl"></i>
            </button>
          )}
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => {
              setInputMessage(e.target.value);
              void notifyVisitorTyping(e.target.value.trim().length > 0);
            }}
            onBlur={() => { void notifyVisitorTyping(false); }}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder={conversationStatus === 'closed' ? 'Atendimento encerrado' : activeQualification ? 'Digite ou selecione uma opção...' : 'Digite sua mensagem...'}
            disabled={conversationStatus === 'closed'}
            className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={conversationStatus === 'closed' || !inputMessage.trim()}
            className="w-10 h-10 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Enviar mensagem"
          >
            <i className="ri-send-plane-fill text-lg"></i>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
